import { BLOG } from "@/_constant";
import blogData from "./blog.json";
import type { BlogArticle, BlogArticleJson, BlogCategory, BlogVideo, BlogVideoJson } from "@/types/blog";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=80";

type BlogDataJson = {
  blog?: BlogArticleJson[];
  youtube_videos?: BlogVideoJson[];
};

const slugify = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeCategory = (category: BlogArticleJson["category"]): BlogCategory[] => {
  if (!category?.length) {
    return [{ categoryId: "general", categoryName: "General", categorySlug: "general" }];
  }

  return category.map((item) => {
    const categoryName = item.categoryName || "General";
    return {
      categoryId: String(item.categoryId || slugify(categoryName)),
      categoryName,
      categorySlug: item.categorySlug || slugify(categoryName),
    };
  });
};

const normalizeArticle = (article: BlogArticleJson): BlogArticle => ({
  title: article.title,
  subtitle: article.subtitle,
  slug: article.slug,
  date: article.date,
  category: normalizeCategory(article.category),
  thumbnailUrl: article.thumbnail_url || FALLBACK_IMAGE,
  bannerImageUrl: article.banner_image_url || article.thumbnail_url || FALLBACK_IMAGE,
  authors: article.author || [],
  tags: article.tags || [],
  summary: article.summary || article.description?.split("\n")[0] || "",
  description: article.description || "",
  similarPosts: article.similar_posts || [],
  isRecent: article.is_recent,
  isFeatured: article.features,
});

const normalizeVideo = (video: BlogVideoJson): BlogVideo => ({
  title: video.title,
  videoLink: video.video_link,
  createdOn: video.created_on,
  seqNo: video.seq_no || 0,
  description: video.description,
});

const normalizedArticles = ((blogData as BlogDataJson).blog || [])
  .map(normalizeArticle)
  .sort((a, b) => new Date(b.date || "").getTime() - new Date(a.date || "").getTime());

const normalizedVideos = ((blogData as BlogDataJson).youtube_videos || [])
  .map(normalizeVideo)
  .sort((a, b) => a.seqNo - b.seqNo || new Date(b.createdOn || "").getTime() - new Date(a.createdOn || "").getTime());

export const getPrimaryCategory = (article: BlogArticle) => article.category[0];

export const getArticlePath = (article: BlogArticle) =>
  `${BLOG}/${getPrimaryCategory(article).categorySlug}/${article.slug}`;

export const getAllBlogArticles = () => normalizedArticles;

export const getBlogCategories = () => {
  const categories = new Map<string, BlogCategory>();

  normalizedArticles.forEach((article) => {
    article.category.forEach((category) => {
      categories.set(category.categorySlug, category);
    });
  });

  return Array.from(categories.values()).sort((a, b) => a.categoryName.localeCompare(b.categoryName));
};

export const getBlogArticlesByCategory = (categorySlug?: string) => {
  if (!categorySlug) return getAllBlogArticles();
  return normalizedArticles.filter((article) =>
    article.category.some((category) => category.categorySlug === categorySlug)
  );
};

export const getBlogArticle = (categorySlug = "", articleSlug = "") =>
  normalizedArticles.find(
    (article) =>
      article.slug === articleSlug &&
      article.category.some((category) => category.categorySlug === categorySlug)
  );

export const getRecentBlogArticles = (limit = 3) =>
  [...normalizedArticles]
    .sort((a, b) => {
      if (!!a.isRecent !== !!b.isRecent) return a.isRecent ? -1 : 1;
      return new Date(b.date || "").getTime() - new Date(a.date || "").getTime();
    })
    .slice(0, limit);

export const getFeaturedBlogArticles = (limit?: number) => {
  const featuredArticles = normalizedArticles.filter((article) => article.isFeatured);
  return typeof limit === "number" ? featuredArticles.slice(0, limit) : featuredArticles;
};

export const getBlogVideos = () => normalizedVideos;

export const searchBlogArticles = (query: string) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return normalizedArticles;

  return normalizedArticles.filter((article) =>
    [article.title, article.subtitle, article.summary, article.description, ...article.tags]
      .filter(Boolean)
      .some((value) => value?.toLowerCase().includes(normalizedQuery))
  );
};

export const getRelatedBlogArticles = (article: BlogArticle, limit = 3) => {
  const similarPosts = article.similarPosts
    .map((postPath) => {
      const [categorySlug, slug] = postPath.replace(/^\/blog\//, "").replace(/^\//, "").split("/");
      return getBlogArticle(categorySlug, slug);
    })
    .filter((item): item is BlogArticle => !!item && item.slug !== article.slug);

  if (similarPosts.length >= limit) return similarPosts.slice(0, limit);

  const categorySlugs = article.category.map((category) => category.categorySlug);
  const fallback = normalizedArticles.filter(
    (item) =>
      item.slug !== article.slug &&
      item.category.some((category) => categorySlugs.includes(category.categorySlug)) &&
      !similarPosts.some((similarPost) => similarPost.slug === item.slug)
  );

  return [...similarPosts, ...fallback].slice(0, limit);
};

export const getNextBlogArticle = (article: BlogArticle) => {
  const relatedArticle = getRelatedBlogArticles(article, 1)[0];
  if (relatedArticle) return relatedArticle;

  const currentIndex = normalizedArticles.findIndex((item) => item.slug === article.slug);
  return normalizedArticles[currentIndex + 1] || normalizedArticles[0];
};
